/* =====================================================================
   AstaLab 2.0 — entrypoint condiviso dell'applicazione.

   Router minimo basato su hash (#/login, #/register, ...): stesso file,
   stesse pagine, sia nel bundle del deploy reale sia nel bundle
   Artifact — l'UNICA differenza fra i due è quale BackendAdapter
   restituisce getBackend() (vedi backend/bootstrap.js), mai una
   condizione scritta qui dentro.
   ===================================================================== */
import { getBackend, getCatalogRepository } from "./backend/bootstrap.js";
import { AuctionRepository } from "./repository/AuctionRepository.js";
import { renderDemoModeBanner } from "./ui/components/DemoModeBanner.js";
import { renderLoginPage } from "./ui/pages/LoginPage.js";
import { renderRegisterPage } from "./ui/pages/RegisterPage.js";
import { renderForgotPasswordPage } from "./ui/pages/ForgotPasswordPage.js";
import { renderResetPasswordPage } from "./ui/pages/ResetPasswordPage.js";
import { renderDashboardPage } from "./ui/pages/DashboardPage.js";
import { renderAuctionDetailPage } from "./ui/pages/AuctionDetailPage.js";
import { renderCatalogPage } from "./ui/pages/CatalogPage.js";

function parseRoute(hash){
  const path = (hash || "#/login").replace(/^#/, "") || "/login";
  const auctionMatch = path.match(/^\/auction\/(.+)$/);
  if(auctionMatch) return { name:"auction", auctionId: decodeURIComponent(auctionMatch[1]) };
  const known = ["/login","/register","/forgot-password","/reset-password","/dashboard","/catalog"];
  return { name: known.includes(path) ? path : "/login" };
}

/* Revisione r13 (audit, "refresh sul wrapper Claude Artifact torna sempre a /login"): il wrapper
   Claude Artifact, a un F5 reale, può ricreare il documento interno senza preservare il suo hash —
   condizione che frontend/index.html locale (page.reload() in Puppeteer) non riproduce, perché lì
   l'hash sopravvive sempre al reload. Queste funzioni ripristinano, quando possibile, l'ultima
   route AUTENTICATA e VALIDATA (mai una stringa arbitraria) vista da QUESTO utente — mai da un
   altro (isolamento per userId, più pulizia esplicita al logout). Storage separato dallo store
   dell'adapter demo: questo meccanismo è adapter-agnostico (nessun `if(adapter.kind===...)`) e vale
   identico sia per ArtifactDemoBackendAdapter sia per SupabaseBackendAdapter, entrambi già dietro lo
   stesso contratto BackendAdapter (getCurrentUser/onAuthStateChange). */
const LAST_ROUTE_STORAGE_KEY = "astalab_2_last_route_v1";
const RESTORABLE_ROUTE_NAMES = new Set(["/dashboard", "/catalog", "auction"]);

function routeToPath(route){
  return route.name === "auction" ? "/auction/" + encodeURIComponent(route.auctionId) : route.name;
}
function loadLastRoutePath(userId){
  try{
    if(typeof localStorage === "undefined" || !userId) return null;
    const raw = localStorage.getItem(LAST_ROUTE_STORAGE_KEY);
    if(!raw) return null;
    const parsed = JSON.parse(raw);
    if(!parsed || parsed.userId !== userId || typeof parsed.path !== "string") return null;
    // Ri-valida SEMPRE il path salvato attraverso lo stesso parser delle route reali: se non
    // corrisponde a una route interna nota e ripristinabile, non viene mai usato (mai una stringa
    // arbitraria, mai una route pubblica come /register).
    const route = parseRoute("#" + parsed.path);
    if(!RESTORABLE_ROUTE_NAMES.has(route.name)) return null;
    return parsed.path;
  }catch(e){ return null; }
}
function saveLastRoutePath(userId, path){
  try{
    if(typeof localStorage === "undefined" || !userId) return;
    localStorage.setItem(LAST_ROUTE_STORAGE_KEY, JSON.stringify({ userId, path }));
  }catch(e){ /* storage pieno/non disponibile: nessun ripristino, non fatale */ }
}
function clearLastRoutePath(){
  try{
    if(typeof localStorage !== "undefined") localStorage.removeItem(LAST_ROUTE_STORAGE_KEY);
  }catch(e){ /* niente da pulire se lo storage non è disponibile */ }
}

/* Round alpha.6 P0.2-A (ALPHA6_P0_2_LIVE_POLLING_DESIGN.md §5 "Async route-render staleness" —
   CORREZIONE I, ripresa anche da CORREZIONE E+J per SIGNED_OUT): serializzazione/coalescing dei
   render di route, estratta come primitive PURA e testabile (nessuna dipendenza da
   window/document/adapter/pagine reali) — mountApp() sotto la usa passandole renderRouteOnce(), che
   legge window.location.hash SOLO al momento in cui viene effettivamente invocata (ogni giro del
   ciclo qui sotto la richiama da capo: mai un hash catturato una volta sola).

   Perché serve: renderCurrentRoute() è async (attende adapter/repository) — se un secondo
   hashchange (o un SIGNED_OUT) arriva mentre il render precedente è ancora a metà di un proprio
   await, senza questa serializzazione partirebbe una seconda esecuzione in parallelo, entrambe
   scriverebbero su pageRoot in un ordine imprevedibile, e il cleanup della pagina più vecchia
   potrebbe risultare applicato per ultimo (o mai). Con questo lock+coalescing: un render già in
   corso fa sì che una richiesta concorrente si limiti a marcare una destinazione pending e tornare
   SENZA scrivere nulla; quando il render in corso termina, il ciclo nota la destinazione pending e fa
   subito un altro giro (che rilegge lo stato più recente) — mai più di un render "vincitore" per
   volta, mai un cleanup della pagina sbagliata.

   Round alpha.6 P0.2-B3 (CORREZIONE V — differential browser control B2, cluster "Dashboard join"):
   il coalescer di CORREZIONE I/S NON conosceva il TARGET (route/destinazione) di ciascuna richiesta —
   trattava qualunque trigger concorrente come "un altro giro necessario", incluso un secondo trigger
   per la STESSA destinazione già in corso o già correntemente renderizzata con successo. Il caso
   concreto: SIGNED_OUT (§5, CORREZIONE E+J) chiama `navigate("/login")` (che muta l'hash, generando
   un `hashchange` ASINCRONO) e SUBITO DOPO una richiesta SINCRONA esplicita per la stessa
   destinazione "/login" — il coalescer precedente trattava il `hashchange` successivo come un
   SECONDO giro necessario, producendo due render pieni di `/login` in rapida successione: un secondo
   `container.innerHTML` che invalida (in Puppeteer, "Node is detached from document") qualunque
   riferimento DOM ottenuto dal primo render. `getRenderKey()` (parametro nuovo, minimo) risolve
   questo SENZA una seconda state machine: ogni richiesta viene identificata dalla destinazione che
   rappresenta (in produzione, `window.location.hash || "#/login"`, letta fresca ad ogni chiamata —
   mai un valore catturato una volta), e il coalescer stesso decide se è realmente necessario un
   nuovo giro confrontando quella chiave con lo stato che già conosce (`activeRenderKey`/`pendingKey`/
   `successfulKey` sotto) — mai un flag fragile tipo `ignoreNextHashchange`. */
export function createRouteRenderCoalescer(renderRouteOnce, getRenderKey){
  let renderInFlight = false;
  let activeRenderKey = null;    // la destinazione ATTUALMENTE in corso di rendering, o null
  let pendingKey = null;         // la destinazione PIÙ RECENTE richiesta mentre una diversa è in corso, o null
  let successfulKey = null;      // l'ultima destinazione renderizzata con successo (stato DOM noto corrente)
  // Round alpha.6 P0.2-B3.5 (CORREZIONE AA1): pendingKey rappresenta SOLO la destinazione PIÙ RECENTE
  // che richiede un passaggio successivo — un ritorno esplicito ad activeRenderKey lo cancella
  // legittimamente (W1). Ma cancellare pendingKey cancella anche l'unica traccia che il pass ATTIVO
  // fosse stato, nel frattempo, superato da una richiesta diversa — informazione necessaria per
  // decidere se un SUO rigetto sia stale, indipendentemente da dove l'hash sia poi tornato. Flag
  // sticky per-pass (mai una seconda state machine): true dal momento in cui una richiesta per una
  // destinazione diversa dall'active arriva, fino all'inizio del PROSSIMO vero passaggio del ciclo —
  // mai cancellato da un successivo ritorno all'active.
  let activeWasSuperseded = false;
  let routeRenderGeneration = 0;
  let currentPageCleanup = null;

  async function renderCurrentRoute(){
    const requestedKey = getRenderKey();

    if(renderInFlight){
      // CORREZIONE V — "same target already rendering": una richiesta per la STESSA destinazione già
      // in corso non deve mai produrre un secondo passaggio (copre sia il duplicato hashchange dopo
      // una richiesta sincrona esplicita, sia l'ordine opposto). Una richiesta per una destinazione
      // DIVERSA sovrascrive sempre il pending precedente — "latest route wins", mai una coda.
      //
      // Round alpha.6 P0.2-B3.1 (CORREZIONE W1): pendingKey rappresenta SEMPRE la destinazione PIÙ
      // RECENTE che richiede un passaggio SUCCESSIVO a quello già in corso — mai una destinazione
      // storica. Se l'ultima richiesta osservata torna a coincidere con activeRenderKey (A in-flight
      // → B richiesta → A richiesta di nuovo, PRIMA che A si risolva), un pending B residuo andrebbe
      // ANNULLATO, non lasciato lì: A che sta già per completarsi produrrà comunque lo stato
      // corrispondente a quella stessa destinazione — un secondo passaggio per B non è mai stato
      // realmente l'ultima cosa richiesta, e un successivo passaggio per A (dopo che A stessa è già
      // finita) sarebbe un secondo render ridondante dello stesso identico target.
      //
      // Round alpha.6 P0.2-B3.5 (CORREZIONE AA1): l'annullamento di pendingKey sopra è corretto per il
      // FOLLOW-UP (nessun giro extra serve se A completa comunque con successo) — ma activeWasSuperseded
      // NON va toccata qui in nessun caso: una volta che una richiesta per una destinazione diversa
      // dall'active è arrivata, il pass attivo resta "superato" per tutta la propria durata,
      // indipendentemente da dove l'hash sia poi tornato — serve al catch sotto per riconoscere un
      // eventuale rigetto di QUESTO stesso pass come stale.
      if(requestedKey === activeRenderKey){
        pendingKey = null;
      }else{
        pendingKey = requestedKey;
        activeWasSuperseded = true;
      }
      return;   // NESSUNA scrittura qui — questa chiamata non "corre" con quella in corso
    }

    // CORREZIONE V — "same target already successfully current": nessun render in corso, ma la
    // destinazione richiesta è già quella con cui il DOM è stato aggiornato l'ultima volta con
    // successo (caso obbligatorio: un duplicato hashchange che arriva DOPO che il render diretto è
    // già completato — dedup del solo pendingKey/in-flight non basterebbe qui, perché non c'è alcun
    // render in-flight da coalescere).
    if(requestedKey === successfulKey) return;

    renderInFlight = true;
    let keyToRender = requestedKey;
    try{
      while(keyToRender !== null){
        activeRenderKey = keyToRender;
        pendingKey = null;
        // Round alpha.6 P0.2-B3.5 (CORREZIONE AA1): ogni vero passaggio di render — sia il primo, sia
        // un retry dopo un rigetto stale — riparte con uno stato di supersessione pulito.
        activeWasSuperseded = false;
        routeRenderGeneration++;
        // Round alpha.6 P0.2-B3.1 (CORREZIONE W2): "successful/current key" deve significare "la
        // route il cui render con successo è ANCORA la pagina montata", mai "l'ultima route riuscita
        // in un punto qualunque della storia". Il momento in cui iniziamo davvero a sostituire la
        // pagina corrente è esattamente questo passaggio del ciclo.
        //
        // Round alpha.6 P0.2-B3.2 (CORREZIONE X1 — source audit indipendente): renderRouteOnce()
        // restituisce un cleanup reale SOLO per AuctionDetailPage.js — login/register/reset/
        // dashboard/catalog restituiscono sempre undefined. Il codice precedente invalidava
        // successfulKey SOLO dentro `if(currentPageCleanup)`, quindi per una route senza cleanup la
        // vecchia key restava fantasma valida anche dopo che questo stesso giro aveva già iniziato a
        // sostituirla — un fallimento non-stale del nuovo render avrebbe lasciato una futura richiesta
        // della vecchia destinazione erroneamente deduplicata. L'invocazione del cleanup (se esiste) e
        // l'invalidazione della key sono due cose distinte: la prima è condizionata alla sua
        // esistenza, la seconda NON deve esserlo mai.
        if(currentPageCleanup){ currentPageCleanup(); currentPageCleanup = null; }
        successfulKey = null;
        // Round alpha.6 P0.2-A1 (CORREZIONE S): un rigetto di QUESTO giro non deve mai sopprimere un
        // giro successivo già richiesto (una destinazione più nuova, letta da un trigger arrivato
        // mentre questo giro era ancora in corso — hashchange o SIGNED_OUT).
        //
        // Round alpha.6 P0.2-B3.2 (CORREZIONE X3): renderRouteOnce() riceve `activeRenderKey`
        // esplicitamente — la stessa identica key appena assegnata sopra — invece di rileggere da sé
        // window.location.hash. La route effettivamente renderizzata e la key marcata successful sotto
        // sono quindi la STESSA variabile, non due letture indipendenti che potrebbero divergere.
        //
        // Round alpha.6 P0.2-B3.4 (CORREZIONE Z1 — sostituisce X4/Y1: la riconciliazione non è più
        // subordinata a pendingKey): in un browser reale `window.location.hash = ...` è sincrono, ma il
        // relativo evento `hashchange` è SEMPRE consegnato in un task successivo — un secondo hashchange
        // può quindi non essere ancora arrivato (pendingKey ancora null) nel momento esatto in cui
        // QUESTO await si risolve, pur essendo l'hash reale già mutato. Condizionare la lettura di
        // getRenderKey() all'esistenza di un pendingKey (come faceva X4/Y1) presuppone implicitamente
        // che una mutazione dell'hash sia sempre già stata seguita dalla delivery del proprio evento —
        // falso in generale. getRenderKey() viene quindi riletto QUI, incondizionatamente, sia sul ramo
        // di successo sia su quello di rigetto, ed è questa singola lettura (mai una seconda state
        // machine) a decidere il giro successivo in entrambi i casi:
        //   successo:  latestKey coincide con successfulKey (appena assegnata)  -> nessun giro successivo
        //              latestKey diverso da successfulKey                       -> renderizza latestKey
        //   rigetto:   vedi CORREZIONE AA2 sotto per la condizione esatta di staleness.
        //
        // Round alpha.6 P0.2-B3.5 (CORREZIONE AA1/AA2 — sostituisce la condizione di staleness di Z1):
        // Z1 originariamente considerava un rigetto stale se `pendingKey !== null` al momento del
        // rigetto — ma pendingKey può tornare null PRIMA che il pass rigetti, per un motivo del tutto
        // legittimo (W1: un ritorno esplicito all'active durante il volo annulla il pending, perché non
        // serve più ALCUN giro successivo se questo pass completa con successo). Quel caso è distinto da
        // "questo pass non è mai stato superato": A in-flight → B richiesta (pendingKey=B) → A richiesta
        // di nuovo (pendingKey annullato a null, W1) → A rigetta — pendingKey!=null non lo vedrebbe più,
        // ma il pass ERA comunque stato superato per un istante, e il suo fallimento resta stale.
        // activeWasSuperseded (sticky per-pass, mai cancellata da un ritorno all'active — dichiarata e
        // impostata sopra) risponde alla domanda corretta: non "esiste ORA un pending diverso?" ma
        // "questo pass è mai stato superato, in un punto qualunque della propria durata?". Combinata con
        // `latestKey !== activeRenderKey` (l'hash reale è già avanzato, anche senza alcun trigger mai
        // consegnato — CORREZIONE Z1/Z3), le due condizioni coprono l'intero spazio dei rigetti stale
        // senza sovrapposizioni ridondanti: se nessuna delle due è vera, il rigetto è genuino e si
        // propaga esattamente come prima (CORREZIONE S invariata).
        let cleanup;
        let latestKey;
        try{
          cleanup = await renderRouteOnce(activeRenderKey);
          successfulKey = activeRenderKey;   // aggiornato SOLO su successo — mai su un giro rigettato
          latestKey = getRenderKey();
        }catch(e){
          latestKey = getRenderKey();
          const newerRouteExists = activeWasSuperseded || latestKey !== activeRenderKey;
          if(!newerRouteExists) throw e;
          cleanup = undefined;
        }
        currentPageCleanup = cleanup || null;   // applicato SOLO se questo è ancora l'ultimo giro utile
        keyToRender = (latestKey !== null && latestKey !== successfulKey) ? latestKey : null;
      }
    } finally {
      renderInFlight = false;
      activeRenderKey = null;
      pendingKey = null;
      activeWasSuperseded = false;
    }
  }

  // CORREZIONE E+J: SIGNED_OUT deve poter fermare il cleanup della pagina corrente IMMEDIATAMENTE se
  // già disponibile — stessa primitive, mai una seconda state machine.
  // Round alpha.6 P0.2-B3.1 (CORREZIONE W2): stessa motivazione del reset dentro il ciclo sopra —
  // disporre la pagina associata a successfulKey (da QUESTA via esterna, non da un nuovo render)
  // deve invalidare quella key allo stesso modo, altrimenti una futura richiesta della stessa
  // destinazione verrebbe deduplicata contro una pagina che non è più realmente montata.
  // Round alpha.6 P0.2-B3.2 (CORREZIONE X2): la semantica route-level non è "invalidare successfulKey
  // solo se esiste per caso un cleanup" — è "dopo forceCleanupNow(), nessuna route precedente può
  // essere considerata ancora definitivamente corrente". Login/Dashboard/Catalog non hanno mai un
  // cleanup: l'invalidazione della key resta comunque incondizionata, esattamente come nel ciclo sopra.
  function forceCleanupNow(){
    if(currentPageCleanup){ currentPageCleanup(); currentPageCleanup = null; }
    successfulKey = null;
  }

  return {
    renderCurrentRoute,
    forceCleanupNow,
    getGeneration: () => routeRenderGeneration,
    isRenderInFlight: () => renderInFlight,
    getSuccessfulKey: () => successfulKey   // CORREZIONE W3: espone la key marcata successful, per testare che coincida con quella realmente renderizzata
  };
}

export async function mountApp(rootEl){
  const { adapter, env } = await getBackend();
  const repository = new AuctionRepository(adapter);
  const { repository: catalogRepository } = await getCatalogRepository();

  rootEl.innerHTML =
    '<div class="app-shell-2">' +
      '<header class="topbar-2"><strong>AstaLab</strong><span class="version-tag">' + env.appVersion + "</span></header>" +
      renderDemoModeBanner(env) +
      '<main id="pageRoot"></main>' +
    "</div>";
  const pageRoot = rootEl.querySelector("#pageRoot");

  function navigate(path){ window.location.hash = "#" + path; }
  const ctx = { adapter, repository, catalogRepository, env, navigate };

  // Round alpha.6 P0.2-A (§5/CORREZIONE I): il corpo di un singolo giro di render, invocato SEMPRE
  // da capo (mai un hash catturato una volta) dal coalescer sotto — restituisce l'eventuale cleanup
  // della pagina (oggi solo AuctionDetailPage.js ne restituisce uno, §5-bis), o undefined per le
  // altre pagine (invariate).
  //
  // Round alpha.6 P0.2-B3.2 (CORREZIONE X3 — chiude realmente la key↔render divergence, sostituendo
  // l'argomento "same tick" di CORREZIONE W3): quell'argomento provava che l'ASSEGNAZIONE di
  // `activeRenderKey` e una lettura indipendente di `window.location.hash` non potessero divergere
  // nello stesso istante sincrono — ma non copriva un PASSAGGIO SUCCESSIVO dopo un `await`: fra la
  // riconciliazione di `keyToRender` nel coalescer e l'esecuzione di questa funzione può non esserci
  // più alcuna garanzia "same tick" se in mezzo si trovasse un'altra lettura indipendente di
  // `window.location.hash`. La correzione è strutturale, non temporale: questa funzione riceve
  // `renderKey` come PARAMETRO ESPLICITO dal coalescer (la stessa variabile che diventa
  // `activeRenderKey` e, solo se il render riesce, `successfulKey`) e non rilegge mai da sé
  // `window.location.hash` per decidere la route — la stessa identica key determina quindi SEMPRE, per
  // costruzione, sia quale route viene renderizzata sia quale key viene marcata successful: non due
  // canali che potrebbero divergere, un solo valore condiviso.
  async function renderRouteOnce(renderKey){
    const route = parseRoute(renderKey);
    if(RESTORABLE_ROUTE_NAMES.has(route.name)){
      // Round Beta-P3-4 (design D/D2, accettato): lettura OPZIONALE (solo per decidere se
      // persistere "ultima route") — un'incertezza qui (vedi BackendAdapter.js/
      // SupabaseBackendAdapter.js) non deve MAI impedire il render specifico della route sotto, che
      // esegue comunque la propria verifica autoritativa e il proprio recupero veritiero. Nessuna UI
      // dedicata qui: si salta semplicemente la persistenza opzionale.
      try{
        const user = await adapter.getCurrentUser();
        if(user) saveLastRoutePath(user.id, routeToPath(route));
      }catch(err){ /* incertezza sulla sessione: nessuna persistenza "ultima route" questo giro, mai un blocco del render sotto */ }
    }
    if(route.name === "auction"){ return await renderAuctionDetailPage(pageRoot, ctx, route.auctionId); }
    if(route.name === "/login"){ renderLoginPage(pageRoot, ctx); return; }
    if(route.name === "/register"){ renderRegisterPage(pageRoot, ctx); return; }
    if(route.name === "/forgot-password"){ renderForgotPasswordPage(pageRoot, ctx); return; }
    if(route.name === "/reset-password"){ renderResetPasswordPage(pageRoot, ctx); return; }
    if(route.name === "/dashboard"){ await renderDashboardPage(pageRoot, ctx); return; }
    if(route.name === "/catalog"){ await renderCatalogPage(pageRoot, ctx); return; }
  }

  // CORREZIONE V: la render key reale — letta fresca ad ogni chiamata dal coalescer stesso (mai
  // catturata una volta sola qui), tramite questa stessa funzione passata come getRenderKey.
  //
  // Round alpha.6 P0.2-B3.2 (CORREZIONE X3 — correzione di un commento stale, nessuna modifica di
  // comportamento): getRenderKey() sopra legge l'hash CORRENTE al momento in cui il coalescer la
  // invoca; il coalescer sceglie da lì `activeRenderKey` e la passa esplicitamente a
  // renderRouteOnce(activeRenderKey) sopra — che NON rilegge più da sé window.location.hash (vedi il
  // commento su renderRouteOnce() sopra). La destinazione realmente renderizzata proviene quindi
  // sempre da questa stessa chiamata a getRenderKey(), mai da una seconda lettura indipendente.
  const routeCoalescer = createRouteRenderCoalescer(renderRouteOnce, () => window.location.hash || "#/login");
  const renderCurrentRoute = routeCoalescer.renderCurrentRoute;

  window.addEventListener("hashchange", renderCurrentRoute);

  // Dopo login/registrazione la sessione può cambiare senza un hashchange (es. sessione
  // ripristinata da Supabase su un altro tab): un SIGNED_IN mentre l'utente è su una pagina
  // pubblica (login/register) lo porta alla dashboard automaticamente; il contrario (sessione
  // persa mentre è sulla dashboard) non forza un redirect qui — ogni pagina che richiede
  // autenticazione ricontrolla da sé getCurrentUser() a ogni render.
  //
  // PASSWORD_RECOVERY (revisione r1, obiettivo 8): l'utente arriva qui con una sessione
  // temporanea generata dal link nell'email di recupero — deve SEMPRE essere portato a
  // /reset-password per impostare la nuova password, indipendentemente da dove si trovava
  // (anche se il link lo riporta su /dashboard per errore/cache). L'adapter demo non emette mai
  // questo evento (non esiste un vero link email), quindi questo ramo è no-op nell'Artifact.
  adapter.onAuthStateChange((event, user, meta) => {
    if(event === "PASSWORD_RECOVERY"){ navigate("/reset-password"); return; }
    // Revisione r13: al logout la route dell'utente uscente non deve mai restare disponibile per
    // un secondo account che effettui l'accesso in seguito nello stesso browser (mai ereditata).
    // Round alpha.6 P0.2-A (§5, CORREZIONE E+J): un SIGNED_OUT può arrivare da QUALUNQUE causa (non
    // solo dal bottone logout di DashboardPage.js) — mai assunto che passi sempre da lì. Riusa la
    // STESSA primitive di coalescing di CORREZIONE I (mai una seconda state machine): forceCleanupNow()
    // ferma subito il cleanup della pagina corrente SE già disponibile; navigate("/login") + una nuova
    // chiamata a renderCurrentRoute() forzano comunque un giro del ciclo anche se un render della
    // vecchia route era ancora in-flight (in quel caso non c'è ancora nulla da fermare qui, ma il
    // giro di coalescing successivo — innescato da questa stessa chiamata — fermerà il cleanup non
    // appena il render stale si risolve, PRIMA che possa mai diventare quello corrente).
    if(event === "SIGNED_OUT"){
      clearLastRoutePath();
      routeCoalescer.forceCleanupNow();
      navigate("/login");
      renderCurrentRoute();
      return;
    }
    const route = parseRoute(window.location.hash);
    const publicRoutes = ["/login","/register","/forgot-password","/reset-password"];
    // Round Beta-P3-3 (design D4, accettato): un SIGNED_IN con meta.isLocalInteractiveAuthInFlight
    // vero significa che QUESTA stessa istanza adapter ha (o aveva, nell'istante dell'evento) una
    // propria chiamata signIn()/signUp() locale in volo per la stessa identità — la pagina che l'ha
    // avviata resta l'unica autorità sulla propria navigazione (LoginPage.js/RegisterPage.js, guardia
    // di generazione+hash), mai duplicata qui. Un SIGNED_IN "davvero esterno" (nessun tentativo
    // locale corrispondente — il caso di sync fra tab che questo ramo esisteva per servire) continua
    // a navigare automaticamente esattamente come prima. Limite noto e accettato (Beta-P3-3D3/D4):
    // un SIGNED_IN esterno genuino per la STESSA identità di un tentativo locale abbandonato non è
    // distinguibile con certezza causale da questo solo segnale — in quel caso la navigazione
    // automatica può essere saltata; la sessione autenticata resta comunque valida.
    if(event === "SIGNED_IN" && user && publicRoutes.includes(route.name) && !(meta && meta.isLocalInteractiveAuthInFlight)){
      navigate("/dashboard");
    }
  });

  // Revisione r13 (audit, "refresh sul wrapper Claude Artifact torna sempre a /login"): un hash
  // assente NON significa più "utente non autenticato, mostra login" — significa solo "nessuna
  // route esplicita nell'URL". Va sempre verificata la sessione reale con adapter.getCurrentUser()
  // PRIMA di scegliere la route di partenza: se esiste un utente autenticato, si ripristina la sua
  // ultima route validata (o /dashboard come minimo garantito), mai /login.
  if(!window.location.hash){
    // Round Beta-P3-4 (design D/D2, accettato): un rigetto qui è un'incertezza sulla sessione, mai
    // un "non autenticato" autoritativo (vedi BackendAdapter.js/SupabaseBackendAdapter.js) — non va
    // MAI trattato come /login. #/dashboard resta la stessa "minimo garantito" già stabilita dalla
    // revisione r13 per il caso autenticato: la pagina protetta esegue da sé la propria verifica
    // veritiera/retry-capace (round Beta-P3-4), mai un secondo componente/modale qui.
    try{
      const user = await adapter.getCurrentUser();
      if(user){
        const lastPath = loadLastRoutePath(user.id);
        window.location.hash = "#" + (lastPath || "/dashboard");
      }else{
        window.location.hash = "#/login";
      }
    }catch(err){
      window.location.hash = "#/dashboard";
    }
  }
  await renderCurrentRoute();

  return ctx; // esposto per i test/diagnostica, mai usato dalla business logic delle pagine
}
